/**
 * Pre-chequeo antes de liberar un lote (depósito con dos correos, 2026-09-10).
 *
 * Muestra el conteo por correo (órdenes, productos, bolsas) y, si hay bolsas mezcladas
 * (paquete de un correo dentro de una bolsa del otro), la lista roja con el detalle.
 * "Liberar" genera un documento de entrega por correo y deja los botones de descarga.
 * Fuente: GET /api/warehouse/batch/:id/release-preview · POST /api/warehouse/batch/:id/release
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, Download, FileText, Loader2, X } from 'lucide-react';
import { api } from '../../../api/client';
import type { WhCourier, WhMode } from '../shared';

interface Preview {
  batch_id: number;
  mode: WhMode;
  by_courier: Array<{ courier: WhCourier | null; orders: number; items: number; bags: string[] }>;
  mixed: Array<{ bag_code: string; ml_order_id: string | null; expected: string; bag_courier: string }>;
}
interface ReleaseRow {
  courier: WhCourier | null;
  url: string;
  orders: number;
  items: number;
}

export function ReleasePreviewModal({
  batchId,
  sellerId,
  canForce,
  onClose,
  onReleased,
}: {
  batchId: number;
  sellerId: number;
  /** puede re-liberar un lote ya liberado (warehouse.reopen_force) */
  canForce?: boolean;
  onClose: () => void;
  onReleased: () => void;
}) {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(true);
  const [releasing, setReleasing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [releases, setReleases] = useState<ReleaseRow[] | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      // seller_id: el servidor valida que el lote sea de ese vendedor (la mesa ve varios vendedores)
      const res = (await api.get<unknown>(
        `/warehouse/batch/${batchId}/release-preview?seller_id=${sellerId}`
      )) as unknown as
        | (Preview & { success: true })
        | { success: false; error?: string };
      if (!alive) return;
      if (res.success) setPreview(res as Preview);
      else setError((res as { error?: string }).error || 'No se pudo leer el lote');
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [batchId]);

  async function release(force: boolean) {
    setReleasing(true);
    setError(null);
    const res = (await api.post<unknown>(`/warehouse/batch/${batchId}/release`, {
      seller_id: sellerId,
      ...(force ? { force: true } : {}),
    })) as unknown as { success: boolean; error?: string; releases?: ReleaseRow[]; url?: string };
    setReleasing(false);
    if (!res.success) {
      setError(res.error || 'No se pudo liberar el lote');
      return;
    }
    const list = res.releases?.length ? res.releases : res.url ? [{ courier: null, url: res.url, orders: 0, items: 0 }] : [];
    setReleases(list);
    if (list.length === 1) window.open(`/api/warehouse/labels/${list[0].url}`, '_blank');
    onReleased();
  }

  const hasMixed = (preview?.mixed.length ?? 0) > 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="w-full max-w-xl rounded-2xl bg-[var(--color-bg-card)] p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-bold text-[var(--color-text)]">
            <FileText size={20} /> Liberar lote #{batchId}
          </h2>
          <button onClick={onClose} className="rounded p-1 hover:bg-[var(--color-bg-hover)]" aria-label="Cerrar">
            <X size={18} className="text-[var(--color-text-secondary)]" />
          </button>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
            <Loader2 size={16} className="animate-spin" /> Revisando el lote…
          </div>
        )}

        {error && (
          <div className="mb-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-200">
            {error}
          </div>
        )}

        {preview && !releases && (
          <>
            <div className="mb-3 space-y-2">
              {preview.by_courier.map((g, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between rounded-lg border-l-4 bg-[var(--color-surface)] px-3 py-2"
                  style={{ borderLeftColor: g.courier?.color ?? '#6b7280' }}
                >
                  <div>
                    <div className="text-sm font-bold" style={{ color: g.courier?.color ?? undefined }}>
                      {g.courier ? g.courier.name : preview.mode === 'multi' ? 'Sin correo definido' : 'Documento único'}
                    </div>
                    <div className="text-xs text-[var(--color-text-secondary)]">
                      Bolsas: {g.bags.length ? g.bags.join(', ') : '—'}
                    </div>
                  </div>
                  <div className="text-right text-sm text-[var(--color-text)]">
                    <div>
                      <b>{g.orders}</b> paquetes
                    </div>
                    <div className="text-xs text-[var(--color-text-secondary)]">{g.items} productos</div>
                  </div>
                </div>
              ))}
            </div>

            {hasMixed && (
              <div className="mb-3 rounded-lg border border-red-300 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
                <div className="mb-1 flex items-center gap-2 text-sm font-bold text-red-800 dark:text-red-200">
                  <AlertTriangle size={16} /> Bolsas mezcladas: mové estos paquetes antes de entregar
                </div>
                <ul className="max-h-40 space-y-0.5 overflow-auto text-xs text-red-900 dark:text-red-100">
                  {preview.mixed.map((m, i) => (
                    <li key={i}>
                      Bolsa <span className="font-mono">{m.bag_code}</span> ({m.bag_courier}) · orden{' '}
                      <span className="font-mono">{m.ml_order_id ?? '—'}</span> → debería ir con{' '}
                      <b>{m.expected}</b>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm">
                Cancelar
              </button>
              <button
                onClick={() => release(false)}
                disabled={releasing}
                className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-bold text-white disabled:opacity-50 ${
                  hasMixed ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {releasing ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                {hasMixed ? 'Liberar igual' : 'Liberar'}
                {preview.by_courier.length > 1 ? ` (${preview.by_courier.length} documentos)` : ''}
              </button>
            </div>
          </>
        )}

        {releases && (
          <div className="space-y-2">
            <div className="text-sm font-semibold text-[var(--color-text)]">Documentos de entrega:</div>
            {releases.map((r, i) => (
              <button
                key={i}
                onClick={() => window.open(`/api/warehouse/labels/${r.url}`, '_blank')}
                className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-bold text-white hover:opacity-90"
                style={{ backgroundColor: r.courier?.color ?? '#7c3aed' }}
              >
                <span className="flex items-center gap-2">
                  <Download size={14} /> PDF {r.courier ? r.courier.name : 'Release'}
                </span>
                {r.orders ? (
                  <span className="text-xs opacity-90">
                    {r.orders} paquetes · {r.items} productos
                  </span>
                ) : null}
              </button>
            ))}
            {canForce ? null : null}
            <div className="flex justify-end pt-1">
              <button onClick={onClose} className="rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm">
                Listo
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
